import { M_Button, M_CheckBox, M_Header, NavigateContext, LoadingContext, M_MessageBoard } from "../../../M_Components"
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { genericEntityService } from "../../../services/genericEntityService";
import './entityPage.css';
import { ItemGrid } from "./ItemsGrid";
import { ItemForm } from "./ItemForm";
import { GridFilters } from "../shared/GridFilters";
import { GridFields } from "../shared/GridFields";
import { FieldTypeEnum } from "../../../enums/fieldTypeEnum";
import { Dialog } from 'primereact/dialog';
import { useGenericEntityContext } from "../context/genericEntityContext";
import { AddCircleOutlineOutlined, Edit, Delete } from '@mui/icons-material';
import { FunctionsComponent } from "../shared/functionsComponent";
import { useErrorHandler } from "../shared/errorHandler";

const PAGE_STATE = {
    EntityView: 0,
    ItemEdit: 1
}

export const EntityPage = ({ onCreateTab }) => {
    const [t, i18n] = useTranslation();
    const currentLanguage = i18n.resolvedLanguage;
    const navCtx = useContext(NavigateContext);
    const ctx = useContext(LoadingContext);
    const { entityId } = useParams();
    const { hasPermission, canAdmin } = useGenericEntityContext();

    const createTab = (url, label) => {
        if (navCtx?.newTab) {
            navCtx.newTab(url, label)
        } else if (onCreateTab) {
            onCreateTab(url, label)
        } else { }
    }

    const [pageState, setPageState] = useState({
        state: PAGE_STATE.EntityView,
        itemId: null,
        canEdit: null
    });
    const [itemForm, setItemForm] = useState({
        form: {},
        errors: {}
    });
    const [entity, setEntity] = useState(null);
    const [filters, setFilters] = useState(null);
    const [hiddenFields, setHiddenFields] = useState([]);
    const [canSaveItem, setCanSaveItem] = useState(false);
    const { handleHttpError } = useErrorHandler();

    const [isSaveClicked, setIsSaveClicked] = useState(false);
    const [openPopup, setOpenPopup] = useState(null); // 'filters', 'fields', or null
    const [selectedItems, setSelectedItems] = useState([]);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [refreshGrid, setRefreshGrid] = useState(0);
    const [openFunctionsDialog, setOpenFunctionsDialog] = useState(false);
    const [functionField, setFunctionField] = useState(null);
    const [gridItems, setGridItems] = useState([]);

    const setPage = (state, itemId, canEdit) => {
        setPageState({
            state: state,
            itemId: itemId,
            canEdit: canEdit
        });

        // Clear selection when returning to EntityView
        if (state === PAGE_STATE.EntityView) {
            setSelectedItems([]);
            sessionStorage.removeItem('selectedGenericItems');
        }

        // When creating a new item (itemId === null), initialize form with default values
        if (state === PAGE_STATE.ItemEdit && itemId === null && entity?.Fields) {
            const defaultForm = {};
            entity.Fields.forEach(field => {
                if (field.Type === FieldTypeEnum.Boolean) {
                    // Always set boolean fields with their default value (or false if not specified)
                    const defaultValue = field.DefaultValue !== null && field.DefaultValue !== undefined
                        ? (typeof field.DefaultValue === 'boolean' ? field.DefaultValue : field.DefaultValue.toString().toLowerCase() === 'true')
                        : false;
                    defaultForm[field.Code] = defaultValue;
                }
                // Add other field types with defaults if needed in the future
            });

            if (Object.keys(defaultForm).length > 0) {
                setItemForm({
                    form: defaultForm,
                    errors: {}
                });
            }
        }
    }

    const fetchEntity = () => {
        genericEntityService.getEntityById(entityId)
            .then(function (response) {
                setEntity(response.data);

                // Always initialize with empty filters (don't restore from sessionStorage)
                // This prevents applying stale filter values when switching entities
                const initialFilters = (response.data.Fields || []).reduce((acc, f) => {
                    acc[f.Code] = "";  // All fields start empty
                    return acc;
                }, {});

                setFilters(initialFilters);

                // Clear any saved filters for this entity to start fresh
                const filterStateKey = `genericEntity_${entityId}_filters`;
                sessionStorage.removeItem(filterStateKey);
            })
            .catch(function (error) {
                handleHttpError(error);
            });
    }

    useEffect(() => {
        setEntity(null);
        fetchEntity();
        setPage(PAGE_STATE.EntityView, null, null);
        setFilters(null);
        setItemForm({
            form: {},
            errors: {}
        });
    }, [entityId])

    useEffect(() => {
        canSaveCallback();
    }, [itemForm.form, itemForm.errors])

    const handleFormChange = (form) => {
        setItemForm(form);
    }

    const handleSave = () => {
        setIsSaveClicked(true);

        if (!canSaveItem) {
            setIsSaveClicked(false);
            return;
        }

        // Check if form contains any File objects
        const hasFiles = Object.values(itemForm.form).some(value => value instanceof File);

        let dataToSend;
        if (hasFiles) {
            // Use FormData only when files are present
            dataToSend = new FormData();
            Object.entries(itemForm.form).forEach(([key, value]) => {
                if (value !== null && value !== undefined) {
                    dataToSend.append(key, value);
                }
            });
        } else {
            // Send as JSON to preserve number types
            dataToSend = { ...itemForm.form };
        }

        if (pageState.itemId === null) {
            genericEntityService.createItem(entity.EntityId, dataToSend, hasFiles)
                .then(function (response) {
                    setPage(PAGE_STATE.EntityView, null, null);
                    setItemForm({
                        form: {},
                        errors: {}
                    });
                    fetchEntity();
                    setIsSaveClicked(false);
                })
                .catch(function (error) {
                    handleHttpError(error);
                    setIsSaveClicked(false);
                });

        } else {
            genericEntityService.updateItemById(entity.EntityId, pageState.itemId, dataToSend)
                .then(function (response) {
                    setPage(PAGE_STATE.EntityView, null, null);
                    setItemForm({
                        form: {},
                        errors: {}
                    });
                    setIsSaveClicked(false);
                })
                .catch(function (error) {
                    handleHttpError(error);
                    setIsSaveClicked(false);
                });

        }

    }
    const handleEdit = (itemId) => {
        setPage(PAGE_STATE.ItemEdit, itemId, false);
    }

    const handleEditSelected = () => {
        if (selectedItems.length === 1) {
            setPage(PAGE_STATE.ItemEdit, selectedItems[0].itemId, true);
        }
    }

    const handleFilterChange = (value) => {
        setFilters(value);

        // Persist only non-empty filters to sessionStorage
        if (entityId) {
            const filterStateKey = `genericEntity_${entityId}_filters`;

            // Filter out empty values before saving
            const nonEmptyFilters = Object.entries(value)
                .filter(([key, val]) => val !== null && val !== undefined && val !== '')
                .reduce((acc, [key, val]) => {
                    acc[key] = val;
                    return acc;
                }, {});

            // Only save if there are actual filters applied
            if (Object.keys(nonEmptyFilters).length > 0) {
                sessionStorage.setItem(filterStateKey, JSON.stringify(nonEmptyFilters));
            } else {
                // Clear if no filters
                sessionStorage.removeItem(filterStateKey);
            }
        }
    };

    const handleDelete = () => {
        genericEntityService.deleteItemById(entity.EntityId, pageState.itemId)
            .then(function (response) {
                setPage(PAGE_STATE.EntityView, null, null);
                setRefreshGrid(prev => prev + 1);
            })
            .catch(function (error) {
                handleHttpError(error);
            });
    }

    const handleOpenFunctions = (fieldCode) => {
        setFunctionField(fieldCode);
        setOpenFunctionsDialog(true);
    };

    const handleBulkDelete = () => {
        if (selectedItems.length === 0) return;

        ctx.setIsLoad(true);
        const itemIds = selectedItems.map(item => item.itemId);

        genericEntityService.deleteMultipleItems(entity.EntityId, itemIds)
            .then(function (results) {
                const successCount = results.filter(r => r.success).length;
                const failCount = results.filter(r => !r.success).length;

                if (failCount > 0) {
                    ctx.toastMessages('warning', `${successCount} ${t('NgeItemsDeleted')}, ${failCount} ${t('NgeFailed')}`, '');
                } else {
                    ctx.toastMessages('success', `${successCount} ${t('NgeItemsDeleted')}`, '');
                }

                setSelectedItems([]);
                sessionStorage.removeItem('selectedGenericItems');
                setOpenDeleteDialog(false);
                setRefreshGrid(prev => prev + 1);
            })
            .catch(function (error) {
                ctx.toastMessages('error', t('NgeDeleteFailed'), '');
            })
            .finally(() => {
                ctx.setIsLoad(false);
            });
    }

    const handleSelectionChange = (items) => {
        setSelectedItems(items);
    }

    const handleDelete_OLD = () => {
        genericEntityService.deleteItemById(entity.EntityId, pageState.itemId)
            .then(function (response) {
                setPage(PAGE_STATE.EntityView, null, null);
            })
            .catch(function (error) {
                handleHttpError(error);
            });
    }

    const canSaveCallback = () => {
        if (entity === null) {
            return;
        }

        if (Object.values(itemForm.errors).some(value => value !== null && value !== undefined)) {
            setCanSaveItem(false);
            return;
        }

        for (const field of entity.Fields) {
            if (field.IsMandatory &&
                (itemForm.form[field.Code] === "" ||
                    itemForm.form[field.Code] === null || itemForm.form[field.Code] === undefined)) {
                setCanSaveItem(false);
                return;
            }
        }

        setCanSaveItem(true);
    }

    const setHiddenFieldsForm = (fields) => {
        setHiddenFields(fields);
    }
    return (
        <>
            <div className="parent">
                <div className={"full-width-header"}>
                    <M_Header
                        top="100%"
                        color="var(--color-white)"
                        element={
                            <div className="entity-header-container">
                                {
                                    pageState.state === PAGE_STATE.EntityView &&
                                    <>
                                        <div className="text-title entity-header-title">
                                            {entity?._captions?.[currentLanguage] ? entity._captions[currentLanguage] : entity?.code}
                                        </div>
                                        <div className="entity-header-buttons">
                                            <M_Button
                                                tooltip={t("NgeFilters")}
                                                label={t("NgeFilters")}
                                                icon={<i className='pi pi-filter'></i>}
                                                onClick={() => setOpenPopup(openPopup === 'filters' ? null : 'filters')}
                                                element={openPopup === 'filters' ? (
                                                    <div>
                                                        <GridFilters
                                                            fields={entity?.Fields}
                                                            filterForm={filters}
                                                            changeFilterCallback={handleFilterChange}
                                                        />
                                                    </div>
                                                ) : null}
                                            />
                                            <M_Button
                                                tooltip={t("NgeFields")}
                                                label={t("NgeFields")}
                                                icon={<i className='pi pi-eye'></i>}
                                                onClick={() => setOpenPopup(openPopup === 'fields' ? null : 'fields')}
                                                element={openPopup === 'fields' ? (
                                                    <div>
                                                        <GridFields
                                                            fields={entity?.Fields?.filter(f => f.type !== FieldTypeEnum.Relation) || []}
                                                            hiddenFields={hiddenFields}
                                                            setHiddenFieldCallback={setHiddenFieldsForm}
                                                            onClose={() => setOpenPopup(null)}
                                                        />
                                                    </div>
                                                ) : null}
                                            />
                                            {(canAdmin || hasPermission('canEditItem')) && (
                                                <M_Button
                                                    tooltip={t("NgeEdit")}
                                                    label={t("NgeEdit")}
                                                    icon={<Edit style={{ color: 'var(--color-blue)' }} />}
                                                    onClick={handleEditSelected}
                                                    disabled={selectedItems.length !== 1}
                                                />
                                            )}
                                            {(canAdmin || hasPermission('canDeleteItem')) && (
                                                <M_Button
                                                    tooltip={t("NgeDelete")}
                                                    label={t("NgeDelete")}
                                                    icon={<Delete style={{ color: 'var(--color-red)' }} />}
                                                    onClick={() => setOpenDeleteDialog(true)}
                                                    disabled={selectedItems.length === 0}
                                                />
                                            )}
                                            {hasPermission('canCreateItem') && (
                                                <M_Button
                                                    tooltip={t("NgeCreate")}
                                                    label={t("NgeCreate")}
                                                    icon={<AddCircleOutlineOutlined style={{ color: 'var(--color-green-light)' }} />}
                                                    onClick={() => setPage(PAGE_STATE.ItemEdit, null, true)}
                                                />
                                            )}
                                        </div>
                                    </>
                                }
                                {
                                    pageState.state === PAGE_STATE.ItemEdit && pageState.canEdit &&
                                    <>
                                        <div className="text-title" style={{ marginRight: "auto" }}>
                                            {entity?._captions?.[currentLanguage] ? entity._captions[currentLanguage] : entity?.code}
                                        </div>
                                        <div style={{ display: 'flex', marginTop: "5px", marginBottom: "5px", marginRight: "5px" }}>
                                            <M_Button
                                                tooltip={t("NgeSave")}
                                                label={t("NgeSave")}
                                                icon={<i className='pi pi-check'></i>}
                                                disabled={!canSaveItem || isSaveClicked}
                                                onClick={() => handleSave()}
                                            />
                                            <M_Button
                                                tooltip={t("NgeCancel")}
                                                label={t("NgeCancel")}
                                                icon={<i className='pi pi-times'></i>}
                                                onClick={() => setPage(PAGE_STATE.EntityView, null, null)}
                                            />
                                        </div>
                                    </>
                                }
                                {
                                    pageState.state === PAGE_STATE.ItemEdit &&
                                    !pageState.canEdit &&
                                    <>
                                        <div className="text-title" style={{ marginRight: "auto" }}>
                                            {entity?._captions?.[currentLanguage] ? entity._captions[currentLanguage] : entity?.code}
                                        </div>
                                        <div style={{ display: 'flex', marginTop: "5px", marginBottom: "5px", marginRight: "5px" }}>
                                            <M_Button
                                                tooltip={t("NgeReturn")}
                                                label={t("NgeReturn")}
                                                icon={<i className='pi pi-arrow-left'></i>}
                                                onClick={() => setPage(PAGE_STATE.EntityView, null, null)}
                                            />
                                        </div>
                                    </>
                                }

                            </div>
                        }
                    />

                </div>
                <div className={"full-area"}>
                    {
                        pageState.state === PAGE_STATE.EntityView &&
                        <ItemGrid
                            entity={entity}
                            itemEditCallback={handleEdit}
                            filters={filters}
                            hiddenFields={hiddenFields}
                            onSelectionChange={handleSelectionChange}
                            onOpenFunctions={handleOpenFunctions}
                            onItemsLoad={setGridItems}
                            key={refreshGrid}
                        />
                    }
                    {
                        pageState.state === PAGE_STATE.ItemEdit &&
                        <>
                            <ItemForm
                                entity={entity}
                                itemId={pageState.itemId}
                                itemForm={itemForm}
                                changeFormCallback={handleFormChange}
                                canEdit={pageState.canEdit}
                            />
                        </>
                    }
                </div>

                {/* Delete Confirmation Dialog */}
                <M_MessageBoard
                    warning
                    open={openDeleteDialog}
                    onClose={() => setOpenDeleteDialog(false)}
                    title={`${t("Delete")} ${selectedItems.length} ${t('NgeItems')}?`}
                    body={
                        <span>
                            {t("NgeConfirmDeleteMultiple")} <b>{selectedItems.length}</b> {t('NgeItems').toLowerCase()}?
                        </span>
                    }
                    footer={
                        <span style={{ display: 'flex' }}>
                            <M_Button
                                normal
                                hoverEffect
                                label={t("ButtonCancel")}
                                backgroundColor={'var(--color-red)'}
                                labelColor={'var(--color-white)'}
                                onClick={() => setOpenDeleteDialog(false)}
                            />
                            <M_Button
                                normal
                                hoverEffect
                                label={t("Delete")}
                                backgroundColor={'var(--color-green)'}
                                labelColor={'var(--color-white)'}
                                onClick={handleBulkDelete}
                            />
                        </span>
                    }
                />

                {/* Functions Dialog */}
                <Dialog
                    visible={openFunctionsDialog}
                    modal
                    header={t("NgeAvailableFunctions")}
                    style={{ width: '30rem' }}
                    onHide={() => setOpenFunctionsDialog(false)}
                >
                    {functionField && gridItems.length > 0 && (
                        <FunctionsComponent
                            items={gridItems}
                            code={functionField}
                        />
                    )}
                </Dialog>

            </div>
        </>
    )
}
